import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { retry, catchError } from 'rxjs/operators';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { environment } from '@environment';
import { IConfigurationContentForm, IFilterFunctions } from '@commons/models';

/**
 * FunctionsService class.
 */
@Injectable({
  providedIn: 'root',
})
export class FunctionsService {
  lobSelected: string = '';
  lobSelected$: BehaviorSubject<string> = new BehaviorSubject<string>('');

  /**
   * Creates an instance of the component.
   * @param http - The http value.
   */
  constructor(private http: HttpClient) {}

  /**
   * Get Data from APIrest
   * @param endpoint endpoint to search data
   * @returns An observable of the response.
   */
  getLobFilter(endpoint: string): Observable<IFilterFunctions> {
    return this.http
      .get<IFilterFunctions>(`${environment.awsServicesBaseUrl}/${endpoint}`)
      .pipe(
        retry(1),
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        }),
      );
  }

  /**
   * This TypeScript function returns a configuration content form based on a given name from a
   * provided array or null if not found.
   * @param {IConfigurationContentForm[] | null | undefined} form - The `form` parameter is an array of
   * `IConfigurationContentForm` objects, or it can be `null` or `undefined`.
   * @param {string} name - The `name` parameter is a string that represents the name of the
   * configuration content form that you want to find within the array of configuration content forms.
   * @returns The `getLobFilterFunction` function returns an `IConfigurationContentForm` object that
   * matches the provided `name` from the `form` array. If no matching object is found, it returns
   * `null`.
   */
  getLobFilterFunction(
    form: IConfigurationContentForm[] | null | undefined,
    name: string,
  ): IConfigurationContentForm | null {
    if (!form) return null;

    return form.find((f) => f.name === name) ?? null;
  }

  /**
   * setLobSelected: set variable lobSelected and Observable
   * @param {string} lob - The lob value.
   */
  setLobSelected(lob: string) {
    this.lobSelected = lob;
    this.lobSelected$.next(this.lobSelected);
  }

  /**
   * getLobSelected: get variable lobSelected and Observable
   * @returns {string} The operation result.
   */
  getLobSelected() {
    return this.lobSelected;
  }

  /**
   * Method to handle error and show alert with information
   * @param error - The error value.
   * @returns The operation result.
   */
  handleError(error: any) {
    let errorMessage = '';
    if (error.error instanceof ErrorEvent) {
      // Get client-side error
      errorMessage = error.error.message;
    } else {
      // Get server-side error
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    return throwError(errorMessage);
  }
}
